import type { NpsReviewState } from '@server/domains/lesson-review/dto'

import type { NpsSurveyState } from '../ui/nps-survey/nps-survey.types'

export type NpsFunnelStep = 'rating' | 'reason' | 'complete'
type Draft = {
  step: NpsFunnelStep
  state: NpsSurveyState
  savedAt: number
  tutorId?: number
  feedbackPending?: boolean
}
const key = (review: NpsReviewState) => `podo:nps:${review.studentId}:${review.classId}`
// Retain unsent input through client navigation even when WebView storage is disabled.
const memoryDrafts = new Map<string, string>()

export const readNpsDraft = (review: NpsReviewState): Draft | null => {
  try {
    let saved = memoryDrafts.get(key(review)) ?? null
    if (saved === null) {
      saved = sessionStorage.getItem(key(review))
    }
    const draft: unknown = JSON.parse(saved ?? 'null')
    if (typeof draft !== 'object' || draft === null) {
      return null
    }
    const value = draft as Partial<Draft>
    const state = value.state
    if (
      !['rating', 'reason', 'complete'].includes(value.step ?? '') ||
      typeof value.savedAt !== 'number' ||
      Date.now() - value.savedAt > 72 * 60 * 60 * 1000 ||
      (value.tutorId !== undefined && value.tutorId !== review.tutorId) ||
      (value.feedbackPending !== undefined && typeof value.feedbackPending !== 'boolean') ||
      !state ||
      !(state.rating === null || [1, 2, 3, 4, 5].includes(state.rating)) ||
      !Array.isArray(state.reasons) ||
      !state.reasons.every((reason) => typeof reason === 'string') ||
      typeof state.reasonText !== 'string' ||
      typeof state.wantBlock !== 'boolean' ||
      typeof state.wantFavorite !== 'boolean'
    ) {
      return null
    }
    return value as Draft
  } catch {
    return null
  }
}

export const writeNpsDraft = (
  review: NpsReviewState,
  step: NpsFunnelStep,
  state: NpsSurveyState,
  feedbackPending = false,
): boolean => {
  const saved = JSON.stringify({ step, state, feedbackPending, tutorId: review.tutorId, savedAt: Date.now() })
  memoryDrafts.set(key(review), saved)
  try {
    sessionStorage.setItem(key(review), saved)
    return true
  } catch {
    return false
  }
}

export const clearNpsDraft = (review: NpsReviewState) => {
  memoryDrafts.delete(key(review))
  try {
    sessionStorage.removeItem(key(review))
  } catch {
    /* Storage can be disabled in a WebView. */
  }
}

export const restoreNpsSurvey = (
  review: NpsReviewState | null | undefined,
  draft: Draft | null = null,
): {
  step: NpsFunnelStep
  state: NpsSurveyState
} => {
  const state: NpsSurveyState = { rating: null, reasons: [], reasonText: '', wantBlock: false, wantFavorite: false }
  if (review?.status === 'SUBMITTED' && review.rating !== null) {
    const hasFeedback = review.reasons.length > 0 || review.reasonText.length > 0
    const matchingDraft = draft?.state.rating === review.rating ? draft : null
    // Restore only unacknowledged edits; matching server contents resolve a lost acknowledgement.
    const pendingFeedback =
      matchingDraft?.step === 'reason' &&
      matchingDraft.feedbackPending === true &&
      (JSON.stringify(matchingDraft.state.reasons) !== JSON.stringify(review.reasons) ||
        matchingDraft.state.reasonText !== review.reasonText)
    const useDraftFeedback = matchingDraft && (pendingFeedback || !hasFeedback)
    return {
      step: pendingFeedback ? 'reason' : hasFeedback || matchingDraft?.step === 'complete' ? 'complete' : 'reason',
      state: {
        ...state,
        rating: review.rating as NpsSurveyState['rating'],
        reasons: useDraftFeedback ? matchingDraft.state.reasons : review.reasons,
        reasonText: useDraftFeedback ? matchingDraft.state.reasonText : review.reasonText,
        wantBlock: matchingDraft?.step === 'complete' ? matchingDraft.state.wantBlock : review.wantBlock,
        wantFavorite: matchingDraft?.state.wantFavorite ?? false,
      },
    }
  }
  return {
    step: 'rating',
    state:
      review?.canWrite && (review.status === null || review.status === 'SKIPPED') && draft?.step === 'rating'
        ? draft.state
        : state,
  }
}
